import React, { useState, useEffect } from 'react';
import { Search, Users, Phone, Mail, User, X, Crown, Wifi, Eye, EyeOff, Wallet, Star } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { customerApi, vpnApi } from '../api';

const STATUS_CONFIG = {
  pending:    { label: '待確認', color: 'bg-yellow-100 text-yellow-700' },
  assigned:   { label: '待接單', color: 'bg-blue-100 text-blue-700' },
  confirmed:  { label: '已接單', color: 'bg-indigo-100 text-indigo-700' },
  preparing:  { label: '備貨中', color: 'bg-orange-100 text-orange-700' },
  delivering: { label: '配送中', color: 'bg-purple-100 text-purple-700' },
  ready:      { label: '待自提', color: 'bg-cyan-100 text-cyan-700' },
  completed:  { label: '已完成', color: 'bg-green-100 text-green-700' },
  cancelled:  { label: '已取消', color: 'bg-gray-100 text-gray-500' }
};

const BALANCE_LOG_TYPES = {
  commission:   { label: '佣金收入', color: 'text-green-600' },
  recharge:     { label: '充值',     color: 'text-green-600' },
  withdraw:     { label: '提币',     color: 'text-red-500' },
  consume:      { label: '消费',     color: 'text-red-500' },
  transfer_in:  { label: '转入',     color: 'text-green-600' },
  transfer_out: { label: '转出',     color: 'text-red-500' },
};

const ENABLE_VPN = process.env.REACT_APP_ENABLE_VPN === 'true';

const CustomersPage = () => {
  const { currentUser } = useApp();
  const isHQ = currentUser?.store_type === 'headquarters';
  const isAdmin = currentUser?.role === 'admin';

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterVip, setFilterVip] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState('info');

  // VIP 设置
  const [showVipDialog, setShowVipDialog] = useState(false);
  const [vipForm, setVipForm] = useState({ is_vip: true, months: 1, note: '' });
  const [vipLoading, setVipLoading] = useState(false);

  // 代理
  const [showSuperVipDialog, setShowSuperVipDialog] = useState(false);
  const [superVipForm, setSuperVipForm] = useState({ is_agent: true, commission_rate: 50 });
  const [superVipLoading, setSuperVipLoading] = useState(false);

  // 余额管理
  const [balanceData, setBalanceData] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ amount: '', type: 'recharge', notes: '' });
  const [adjustLoading, setAdjustLoading] = useState(false);

  // 佣金记录
  const [commissionLogs, setCommissionLogs] = useState([]);
  const [commissionLoading, setCommissionLoading] = useState(false);

  // VPN 相关
  const [vpnSubs, setVpnSubs] = useState([]);
  const [vpnPool, setVpnPool] = useState([]);
  const [showVpnAssign, setShowVpnAssign] = useState(false);
  const [selectedPoolId, setSelectedPoolId] = useState('');
  const [vpnLoading, setVpnLoading] = useState(false);
  const [showVpnUrls, setShowVpnUrls] = useState({});

  useEffect(() => { loadCustomers(); }, [page]);

  useEffect(() => {
    const timer = setTimeout(() => { setPage(1); loadCustomers(); }, 500);
    return () => clearTimeout(timer);
  }, [search, filterVip]);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const params = { search, page, limit: 20 };
      if (filterVip !== '') params.is_vip = filterVip;
      const res = await customerApi.getList(params);
      if (res.success) {
        setCustomers(res.data);
        setPagination(res.pagination);
      }
    } catch (err) {
      console.error('加载客户失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (customer) => {
    setSelectedCustomer(customer);
    setShowDetail(true);
    setDetailLoading(true);
    setDetailTab('info');
    setBalanceData(null);
    setCommissionLogs([]);
    try {
      const res = await customerApi.getDetail(customer.id);
      if (res.success) setSelectedCustomer(res.data);
      if (ENABLE_VPN) {
        const vpnRes = await vpnApi.getCustomerSubscriptions(customer.id);
        if (vpnRes.success) setVpnSubs(vpnRes.data);
      }
    } catch (err) {
      console.error('加载客户详情失败:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const loadBalance = async (customer_id) => {
    setBalanceLoading(true);
    try {
      const res = await customerApi.getBalance(customer_id);
      if (res.success) setBalanceData(res.data);
    } catch (err) {
      console.error('加载余额失败:', err);
    } finally {
      setBalanceLoading(false);
    }
  };

  const loadCommissionLogs = async (customer_id) => {
    setCommissionLoading(true);
    try {
      const res = await customerApi.getCommissionLogs(customer_id);
      if (res.success) setCommissionLogs(res.data);
    } catch (err) {
      console.error('加载佣金记录失败:', err);
    } finally {
      setCommissionLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setDetailTab(tab);
    if (tab === 'balance' && !balanceData) loadBalance(selectedCustomer.id);
    if (tab === 'commission') loadCommissionLogs(selectedCustomer.id);
  };

  // VIP
  const openVipDialog = (customer) => {
    setSelectedCustomer(customer);
    const active = isVipActive(customer);
    setVipForm({ is_vip: !active, months: 1, note: '' });
    setShowVipDialog(true);
  };

  const handleSetVip = async () => {
    if (!selectedCustomer) return;
    setVipLoading(true);
    try {
      const res = await customerApi.setVip(selectedCustomer.id, vipForm);
      if (res.success) {
        alert(res.message);
        setShowVipDialog(false);
        loadCustomers();
        if (showDetail) {
          const detail = await customerApi.getDetail(selectedCustomer.id);
          if (detail.success) setSelectedCustomer(detail.data);
        }
      } else {
        alert(res.message || '操作失败');
      }
    } catch (err) {
      alert(err.message || '操作失败');
    } finally {
      setVipLoading(false);
    }
  };

  // 代理
  const openSuperVipDialog = (customer) => {
    setSelectedCustomer(customer);
    setSuperVipForm({
      is_agent: !customer.is_agent,
      commission_rate: customer.agent_commission_rate || 50
    });
    setShowSuperVipDialog(true);
  };

  const handleSetSuperVip = async () => {
    if (!selectedCustomer) return;
    setSuperVipLoading(true);
    try {
      const res = await customerApi.setSuperVip(selectedCustomer.id, {
        is_super_vip: superVipForm.is_agent,
        commission_rate: superVipForm.commission_rate
      });
      if (res.success) {
        alert(res.message);
        setShowSuperVipDialog(false);
        loadCustomers();
        const detail = await customerApi.getDetail(selectedCustomer.id);
        if (detail.success) setSelectedCustomer(detail.data);
      } else {
        alert(res.message || '操作失败');
      }
    } catch (err) {
      alert(err.message || '操作失败');
    } finally {
      setSuperVipLoading(false);
    }
  };

  // 余额调整
  const handleAdjustBalance = async () => {
    if (!adjustForm.amount || parseFloat(adjustForm.amount) <= 0) {
      alert('请输入有效金额');
      return;
    }
    setAdjustLoading(true);
    try {
      const res = await customerApi.adjustBalance(selectedCustomer.id, adjustForm);
      if (res.success) {
        alert(res.message);
        setShowAdjustDialog(false);
        setAdjustForm({ amount: '', type: 'recharge', notes: '' });
        loadBalance(selectedCustomer.id);
      } else {
        alert(res.message || '操作失败');
      }
    } catch (err) {
      alert(err.response?.data?.message || '操作失败');
    } finally {
      setAdjustLoading(false);
    }
  };

  // VPN
  const openVpnAssign = async () => {
    setVpnLoading(true);
    try {
      const res = await vpnApi.getPoolList({ is_assigned: 0 });
      if (res.success) setVpnPool(res.data.filter(p => p.status === 'active'));
    } catch (err) {
      console.error('加载链接池失败:', err);
    } finally {
      setVpnLoading(false);
    }
    setSelectedPoolId('');
    setShowVpnAssign(true);
  };

  const handleAssignVpn = async () => {
    if (!selectedPoolId) { alert('请选择一个账号'); return; }
    setVpnLoading(true);
    try {
      const res = await vpnApi.assignToCustomer({
        pool_id: parseInt(selectedPoolId),
        customer_id: selectedCustomer.id
      });
      if (res.success) {
        alert('VPN 账号分配成功');
        setShowVpnAssign(false);
        const vpnRes = await vpnApi.getCustomerSubscriptions(selectedCustomer.id);
        if (vpnRes.success) setVpnSubs(vpnRes.data);
      } else {
        alert(res.message || '分配失败');
      }
    } catch (err) {
      alert(err.message || '分配失败');
    } finally {
      setVpnLoading(false);
    }
  };

  const handleUnassignVpn = async (pool_id) => {
    if (!window.confirm('确定取消该 VPN 账号的分配？')) return;
    try {
      const res = await vpnApi.unassignFromCustomer(pool_id);
      if (res.success) {
        alert('已取消分配');
        const vpnRes = await vpnApi.getCustomerSubscriptions(selectedCustomer.id);
        if (vpnRes.success) setVpnSubs(vpnRes.data);
      } else {
        alert(res.message || '操作失败');
      }
    } catch (err) {
      alert('操作失败');
    }
  };

  const toggleVpnUrl = (id) => setShowVpnUrls(prev => ({ ...prev, [id]: !prev[id] }));

  // VIP判断 — 只用 vip_expired_ts
  const isVipActive = (customer) => {
    if (!customer.vip_expired_ts) return false;
    return customer.vip_expired_ts > Math.floor(Date.now() / 1000);
  };

  const formatVipExpiry = (customer) => {
    if (!customer.vip_expired_ts) return '未开通';
    return new Date(customer.vip_expired_ts * 1000).toLocaleDateString('zh-CN');
  };

  return (
    <div className="p-6 space-y-6 overflow-y-auto h-full">
      {/* 头部 */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold">客户管理</h2>
          <p className="text-xs text-gray-600 mt-1">
            {isHQ ? '所有 H5 注册客户' : '下过本店订单的客户'}
            {pagination.total > 0 && <span className="ml-2 text-blue-600">共 {pagination.total} 位</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={filterVip} onChange={e => setFilterVip(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm">
            <option value="">全部客户</option>
            <option value="1">VIP 客户</option>
            <option value="0">普通客户</option>
          </select>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="搜索姓名、手机号、用户名..."
              className="pl-9 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm w-64" />
          </div>
        </div>
      </div>

      {/* 客户列表 */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : customers.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">暂无客户</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">客户</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">联系方式</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">VIP</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">订单数</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">累计消费</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">最近下单</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {customers.map(customer => (
                <tr key={customer.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                        customer.is_agent ? 'bg-purple-100' : isVipActive(customer) ? 'bg-yellow-100' : 'bg-blue-100'
                      }`}>
                        {customer.is_agent
                          ? <Star className="w-5 h-5 text-purple-600" />
                          : isVipActive(customer)
                            ? <Crown className="w-5 h-5 text-yellow-600" />
                            : <User className="w-5 h-5 text-blue-600" />
                        }
                      </div>
                      <div>
                        <div className="flex items-center gap-1">
                          <p className="font-medium text-sm">{customer.name || customer.username || '匿名'}</p>
                          {customer.is_agent === 1 && (
                            <span className="px-1 py-0.5 text-xs rounded bg-purple-100 text-purple-700">代理</span>
                          )}
                        </div>
                        {customer.username && customer.name && (
                          <p className="text-xs text-gray-400">@{customer.username}</p>
                        )}
                        {customer.tg_username && (
                          <p className="text-xs text-blue-400">✈️ @{customer.tg_username}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="space-y-0.5">
                      {customer.phone && (
                        <p className="text-sm flex items-center space-x-1">
                          <Phone className="w-3 h-3 text-gray-400" />
                          <span>{customer.phone}</span>
                        </p>
                      )}
                      {customer.email && (
                        <p className="text-sm flex items-center space-x-1 text-gray-500">
                          <Mail className="w-3 h-3 text-gray-400" />
                          <span>{customer.email}</span>
                        </p>
                      )}
                      {!customer.phone && !customer.email && (
                        <p className="text-xs text-gray-400">未填写</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {isVipActive(customer) ? (
                      <div>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                          <Crown className="w-3 h-3" />VIP
                        </span>
                        <p className="text-xs text-gray-400 mt-0.5">到期 {formatVipExpiry(customer)}</p>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">普通</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {customer.order_count || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="font-medium text-green-600">
                      ${parseFloat(customer.total_spent || 0).toFixed(2)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {customer.last_order_at
                      ? new Date(customer.last_order_at).toLocaleDateString('zh-CN')
                      : '暂无'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => openDetail(customer)}
                        className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1 rounded-lg hover:bg-blue-50">
                        详情
                      </button>
                      {isAdmin && (
                        <button onClick={() => openVipDialog(customer)}
                          className={`text-xs px-2 py-1 rounded-lg ${
                            isVipActive(customer)
                              ? 'text-yellow-600 hover:bg-yellow-50'
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}>
                          {isVipActive(customer) ? '续费' : '开VIP'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {pagination.pages > 1 && (
            <div className="px-4 py-3 border-t flex items-center justify-between">
              <p className="text-sm text-gray-500">
                第 {page} / {pagination.pages} 页，共 {pagination.total} 条
              </p>
              <div className="flex space-x-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-gray-50">上一页</button>
                <button onClick={() => setPage(p => Math.min(pagination.pages, p + 1))} disabled={page === pagination.pages}
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-gray-50">下一页</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 客户详情弹窗 */}
      {showDetail && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="p-5 border-b flex justify-between items-center">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold">{selectedCustomer.name || selectedCustomer.username || '匿名'}</h3>
                {isVipActive(selectedCustomer) && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                    <Crown className="w-3 h-3" />VIP
                  </span>
                )}
                {selectedCustomer.is_agent === 1 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                    <Star className="w-3 h-3" />代理
                  </span>
                )}
              </div>
              <button onClick={() => setShowDetail(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Tab 切换 */}
            <div className="flex border-b">
              {[
                { key: 'info', label: '基本信息' },
                { key: 'balance', label: '余额管理' },
                ...(selectedCustomer.is_agent ? [{ key: 'commission', label: '佣金记录' }] : []),
              ].map(tab => (
                <button key={tab.key} onClick={() => handleTabChange(tab.key)}
                  className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                    detailTab === tab.key
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}>
                  {tab.label}
                </button>
              ))}
            </div>

            {detailLoading ? (
              <div className="flex-1 flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto p-5 space-y-4">

                {/* 基本信息 Tab */}
                {detailTab === 'info' && (
                  <>
                    {/* VIP 信息 */}
                    {isVipActive(selectedCustomer) ? (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-yellow-800 flex items-center gap-1">
                              <Crown className="w-4 h-4" />VIP 会员
                            </p>
                            <p className="text-xs text-yellow-600 mt-1">到期时间：{formatVipExpiry(selectedCustomer)}</p>
                            {selectedCustomer.vip_note && (
                              <p className="text-xs text-yellow-600">备注：{selectedCustomer.vip_note}</p>
                            )}
                          </div>
                          {isAdmin && (
                            <div className="flex gap-2">
                              <button onClick={() => { setShowDetail(false); openVipDialog(selectedCustomer); }}
                                className="text-xs px-3 py-1.5 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200">
                                续费
                              </button>
                              <button onClick={() => { setShowDetail(false); setVipForm({ is_vip: false, months: 1, note: '' }); setShowVipDialog(true); }}
                                className="text-xs px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200">
                                取消
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      isAdmin && (
                        <button onClick={() => { setShowDetail(false); openVipDialog(selectedCustomer); }}
                          className="w-full py-2 border-2 border-dashed border-yellow-300 text-yellow-600 rounded-xl text-sm hover:bg-yellow-50 flex items-center justify-center gap-2">
                          <Crown className="w-4 h-4" />开通 VIP 会员
                        </button>
                      )
                    )}

                    {/* 代理 */}
                    {isAdmin && (
                      <div className={`border rounded-xl p-4 ${selectedCustomer.is_agent ? 'bg-purple-50 border-purple-200' : 'border-gray-200'}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium flex items-center gap-1 text-purple-800">
                              <Star className="w-4 h-4" />代理
                            </p>
                            {selectedCustomer.is_agent === 1 ? (
                              <>
                                <p className="text-xs text-purple-600 mt-1">
                                  优惠码：<span className="font-mono font-bold">{selectedCustomer.agent_code}</span>
                                </p>
                                <p className="text-xs text-purple-600">
                                  分成比例：{selectedCustomer.agent_commission_rate}% | 累计佣金：${parseFloat(selectedCustomer.agent_total_earnings || 0).toFixed(2)} | 推广次数：{selectedCustomer.agent_total_referrals || 0}
                                </p>
                              </>
                            ) : (
                              <p className="text-xs text-gray-400 mt-1">未开通代理</p>
                            )}
                          </div>
                          <button onClick={() => openSuperVipDialog(selectedCustomer)}
                            className={`text-xs px-3 py-1.5 rounded-lg ${
                              selectedCustomer.is_agent
                                ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                            }`}>
                            {selectedCustomer.is_agent ? '取消' : '开通'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* VPN 订阅 */}
                    {ENABLE_VPN && isAdmin && (
                      <div className="border border-gray-200 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-sm font-medium text-gray-700 flex items-center gap-1">
                            <Wifi className="w-4 h-4 text-blue-500" />VPN 订阅
                          </p>
                          <button onClick={openVpnAssign}
                            className="text-xs px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100">
                            + 分配账号
                          </button>
                        </div>
                        {vpnSubs.length === 0 ? (
                          <p className="text-xs text-gray-400 text-center py-2">暂未分配 VPN 账号</p>
                        ) : (
                          <div className="space-y-2">
                            {vpnSubs.map(sub => (
                              <div key={sub.id} className="bg-gray-50 rounded-lg p-3">
                                <div className="flex items-center justify-between mb-2">
                                  <p className="text-sm font-medium text-gray-800">{sub.account_name}</p>
                                  <div className="flex items-center gap-2">
                                    {sub.expired_ts && (
                                      <span className="text-xs text-gray-400">
                                        到期 {new Date(sub.expired_ts * 1000).toLocaleDateString()}
                                      </span>
                                    )}
                                    <button onClick={() => handleUnassignVpn(sub.id)}
                                      className="text-xs text-red-500 hover:text-red-700">取消</button>
                                  </div>
                                </div>
                                <div className="space-y-1">
                                  {[
                                    { label: '通用', url: sub.sub_url },
                                    { label: 'Clash', url: sub.clash_url },
                                    { label: 'V2ray', url: sub.v2ray_url },
                                    { label: 'SS', url: sub.ss_url },
                                    { label: 'Trojan', url: sub.trojan_url },
                                    { label: 'Vless', url: sub.vless_url },
                                  ].filter(item => item.url).map(item => (
                                    <div key={item.label} className="flex items-center gap-2">
                                      <span className="text-xs text-gray-500 w-10 flex-shrink-0">{item.label}</span>
                                      <span className="text-xs text-gray-600 truncate flex-1">
                                        {showVpnUrls[`${sub.id}_${item.label}`] ? item.url : '••••••••••••••••'}
                                      </span>
                                      <button onClick={() => toggleVpnUrl(`${sub.id}_${item.label}`)}
                                        className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                                        {showVpnUrls[`${sub.id}_${item.label}`]
                                          ? <EyeOff className="w-3 h-3" />
                                          : <Eye className="w-3 h-3" />}
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* 账号信息 */}
                    <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                      <p className="text-sm font-medium text-gray-700 mb-2">账号信息</p>
                      {selectedCustomer.phone && (
                        <div className="flex items-center space-x-2 text-sm">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <span>{selectedCustomer.phone}</span>
                        </div>
                      )}
                      {selectedCustomer.email && (
                        <div className="flex items-center space-x-2 text-sm">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <span>{selectedCustomer.email}</span>
                          <span className="text-xs text-green-500">已绑定</span>
                        </div>
                      )}
                      {selectedCustomer.tg_username && (
                        <div className="flex items-center space-x-2 text-sm">
                          <span className="text-blue-500">✈️</span>
                          <span>@{selectedCustomer.tg_username}</span>
                          <span className="text-xs text-green-500">已绑定</span>
                        </div>
                      )}
                    </div>

                    {/* 消费统计 */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-blue-50 rounded-xl p-4 text-center">
                        <p className="text-2xl font-bold text-blue-600">{selectedCustomer.order_count || 0}</p>
                        <p className="text-xs text-gray-500 mt-1">累计订单</p>
                      </div>
                      <div className="bg-green-50 rounded-xl p-4 text-center">
                        <p className="text-2xl font-bold text-green-600">${parseFloat(selectedCustomer.total_spent || 0).toFixed(2)}</p>
                        <p className="text-xs text-gray-500 mt-1">累计消费</p>
                      </div>
                    </div>

                    {/* 最近订单 */}
                    {selectedCustomer.recent_orders?.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">最近订单</p>
                        <div className="space-y-2">
                          {selectedCustomer.recent_orders.map(order => {
                            const statusCfg = STATUS_CONFIG[order.order_status] || STATUS_CONFIG.pending;
                            return (
                              <div key={order.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                                <div>
                                  <p className="text-sm font-medium">{order.order_no}</p>
                                  <p className="text-xs text-gray-400">{new Date(order.created_at).toLocaleDateString('zh-CN')}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-medium text-green-600">${parseFloat(order.total_amount).toFixed(2)}</p>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${statusCfg.color}`}>{statusCfg.label}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* 余额管理 Tab */}
                {detailTab === 'balance' && (
                  <>
                    {balanceLoading ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      </div>
                    ) : balanceData ? (
                      <>
                        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-gray-500 flex items-center gap-1">
                                <Wallet className="w-4 h-4" />当前余额
                              </p>
                              <p className="text-3xl font-bold text-green-600 mt-1">
                                ${parseFloat(balanceData.balance || 0).toFixed(2)}
                                <span className="text-sm font-normal text-gray-400 ml-1">USDT</span>
                              </p>
                            </div>
                            {isAdmin && (
                              <button onClick={() => setShowAdjustDialog(true)}
                                className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                                手动调整
                              </button>
                            )}
                          </div>
                        </div>

                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">余额流水</p>
                          {balanceData.logs?.length === 0 ? (
                            <p className="text-xs text-gray-400 text-center py-4">暂无流水记录</p>
                          ) : (
                            <div className="space-y-2">
                              {balanceData.logs?.map(log => {
                                const typeInfo = BALANCE_LOG_TYPES[log.type] || { label: log.type, color: 'text-gray-600' };
                                return (
                                  <div key={log.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                                    <div>
                                      <p className="text-sm font-medium text-gray-700">{typeInfo.label}</p>
                                      <p className="text-xs text-gray-400">{log.notes}</p>
                                      <p className="text-xs text-gray-400">
                                        {new Date(log.created_at_ts * 1000).toLocaleString()}
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <p className={`text-sm font-bold ${typeInfo.color}`}>
                                        {parseFloat(log.amount) > 0 ? '+' : ''}{parseFloat(log.amount).toFixed(2)}
                                      </p>
                                      <p className="text-xs text-gray-400">余额 ${parseFloat(log.balance_after).toFixed(2)}</p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </>
                    ) : null}
                  </>
                )}

                {/* 佣金记录 Tab */}
                {detailTab === 'commission' && (
                  <>
                    <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                      <p className="text-sm font-medium text-purple-800 flex items-center gap-1">
                        <Star className="w-4 h-4" />代理推广数据
                      </p>
                      <div className="grid grid-cols-3 gap-3 mt-3">
                        <div className="text-center">
                          <p className="text-xl font-bold text-purple-600">{selectedCustomer.agent_total_referrals || 0}</p>
                          <p className="text-xs text-gray-500">推广次数</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xl font-bold text-green-600">${parseFloat(selectedCustomer.agent_total_earnings || 0).toFixed(2)}</p>
                          <p className="text-xs text-gray-500">累计佣金</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xl font-bold text-blue-600">{selectedCustomer.agent_commission_rate}%</p>
                          <p className="text-xs text-gray-500">分成比例</p>
                        </div>
                      </div>
                      <p className="text-xs text-purple-600 mt-2">
                        优惠码：<span className="font-mono font-bold">{selectedCustomer.agent_code}</span>
                      </p>
                    </div>

                    {commissionLoading ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                      </div>
                    ) : commissionLogs.length === 0 ? (
                      <p className="text-xs text-gray-400 text-center py-4">暂无佣金记录</p>
                    ) : (
                      <div className="space-y-2">
                        {commissionLogs.map(log => (
                          <div key={log.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                            <div>
                              <p className="text-sm font-medium text-gray-700">{log.notes || '佣金收入'}</p>
                              <p className="text-xs text-gray-400">{log.related_order_no}</p>
                              <p className="text-xs text-gray-400">
                                {new Date(log.created_at_ts * 1000).toLocaleString()}
                              </p>
                            </div>
                            <p className="text-sm font-bold text-green-600">
                              +${parseFloat(log.amount).toFixed(2)}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* VIP 设置弹窗 */}
      {showVipDialog && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="p-5 border-b flex justify-between items-center">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <Crown className="w-5 h-5 text-yellow-600" />
                {isVipActive(selectedCustomer) ? '续费 VIP' : '开通 VIP'}
              </h3>
              <button onClick={() => setShowVipDialog(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-600">客户：<span className="font-medium">{selectedCustomer.name || selectedCustomer.username}</span></p>
              {isVipActive(selectedCustomer) && (
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="vip_action" checked={vipForm.is_vip}
                      onChange={() => setVipForm({ ...vipForm, is_vip: true })} />
                    <span className="text-sm">续费 VIP</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="vip_action" checked={!vipForm.is_vip}
                      onChange={() => setVipForm({ ...vipForm, is_vip: false })} />
                    <span className="text-sm text-red-600">取消 VIP</span>
                  </label>
                </div>
              )}
              {vipForm.is_vip && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">续费时长</label>
                  <div className="grid grid-cols-4 gap-2">
                    {[1, 3, 6, 12].map(m => (
                      <button key={m} type="button"
                        onClick={() => setVipForm({ ...vipForm, months: m })}
                        className={`py-2 rounded-lg text-sm font-medium border transition-colors ${
                          vipForm.months === m
                            ? 'bg-yellow-500 text-white border-yellow-500'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-yellow-400'
                        }`}>
                        {m}个月
                      </button>
                    ))}
                  </div>
                  {isVipActive(selectedCustomer) && (
                    <p className="text-xs text-gray-400 mt-2">
                      当前到期：{formatVipExpiry(selectedCustomer)}，续费后延长 {vipForm.months} 个月
                    </p>
                  )}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">备注</label>
                <input type="text" value={vipForm.note}
                  onChange={e => setVipForm({ ...vipForm, note: e.target.value })}
                  placeholder="例：购买VIP套餐、赠送会员..."
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-yellow-400" />
              </div>
            </div>
            <div className="p-5 border-t flex gap-3">
              <button onClick={() => setShowVipDialog(false)}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                取消
              </button>
              <button onClick={handleSetVip} disabled={vipLoading}
                className={`flex-1 py-2 rounded-lg text-sm font-medium text-white transition-colors ${
                  vipForm.is_vip ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-red-500 hover:bg-red-600'
                } disabled:opacity-50`}>
                {vipLoading ? '处理中...' : vipForm.is_vip ? '确认开通/续费' : '确认取消'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 代理设置弹窗 */}
      {showSuperVipDialog && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="p-5 border-b flex justify-between items-center">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <Star className="w-5 h-5 text-purple-600" />
                {selectedCustomer.is_agent ? '取消代理' : '开通代理'}
              </h3>
              <button onClick={() => setShowSuperVipDialog(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-600">客户：<span className="font-medium">{selectedCustomer.name || selectedCustomer.username}</span></p>
              {superVipForm.is_agent && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">佣金分成比例 %</label>
                  <div className="grid grid-cols-4 gap-2">
                    {[30, 40, 50, 60].map(r => (
                      <button key={r} type="button"
                        onClick={() => setSuperVipForm({ ...superVipForm, commission_rate: r })}
                        className={`py-2 rounded-lg text-sm font-medium border transition-colors ${
                          superVipForm.commission_rate === r
                            ? 'bg-purple-500 text-white border-purple-500'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400'
                        }`}>
                        {r}%
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    推广用户每购买一次 VIP，推广人获得 {superVipForm.commission_rate}% 佣金
                  </p>
                </div>
              )}
              {!superVipForm.is_agent && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-600">取消后该用户的优惠码将失效，已获得的佣金不受影响。</p>
                </div>
              )}
            </div>
            <div className="p-5 border-t flex gap-3">
              <button onClick={() => setShowSuperVipDialog(false)}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                取消
              </button>
              <button onClick={handleSetSuperVip} disabled={superVipLoading}
                className={`flex-1 py-2 rounded-lg text-sm font-medium text-white transition-colors ${
                  superVipForm.is_agent ? 'bg-purple-500 hover:bg-purple-600' : 'bg-red-500 hover:bg-red-600'
                } disabled:opacity-50`}>
                {superVipLoading ? '处理中...' : superVipForm.is_agent ? '确认开通' : '确认取消'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 余额调整弹窗 */}
      {showAdjustDialog && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="p-5 border-b flex justify-between items-center">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <Wallet className="w-5 h-5 text-blue-600" />手动调整余额
              </h3>
              <button onClick={() => setShowAdjustDialog(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-600">
                客户：<span className="font-medium">{selectedCustomer.name || selectedCustomer.username}</span>
                <span className="ml-2 text-green-600 font-medium">
                  当前余额 ${parseFloat(balanceData?.balance || 0).toFixed(2)}
                </span>
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">操作类型</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'recharge', label: '充值', color: 'bg-green-500' },
                    { value: 'consume', label: '扣款', color: 'bg-red-500' },
                    { value: 'withdraw', label: '提币', color: 'bg-orange-500' },
                  ].map(t => (
                    <button key={t.value} type="button"
                      onClick={() => setAdjustForm({ ...adjustForm, type: t.value })}
                      className={`py-2 rounded-lg text-sm font-medium text-white transition-colors ${
                        adjustForm.type === t.value ? t.color : 'bg-gray-200 text-gray-600'
                      }`}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">金额 (USDT)</label>
                <input type="number" min="0" step="0.01" value={adjustForm.amount}
                  onChange={e => setAdjustForm({ ...adjustForm, amount: e.target.value })}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">备注</label>
                <input type="text" value={adjustForm.notes}
                  onChange={e => setAdjustForm({ ...adjustForm, notes: e.target.value })}
                  placeholder="调整原因..."
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div className="p-5 border-t flex gap-3">
              <button onClick={() => setShowAdjustDialog(false)}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                取消
              </button>
              <button onClick={handleAdjustBalance} disabled={adjustLoading}
                className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                {adjustLoading ? '处理中...' : '确认调整'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VPN 分配弹窗 */}
      {showVpnAssign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="p-5 border-b flex justify-between items-center">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <Wifi className="w-5 h-5 text-blue-600" />分配 VPN 账号
              </h3>
              <button onClick={() => setShowVpnAssign(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-600">
                客户：<span className="font-medium">{selectedCustomer?.name || selectedCustomer?.username}</span>
              </p>
              {vpnLoading ? (
                <div className="text-center py-4">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
              ) : vpnPool.length === 0 ? (
                <div className="text-center py-4 text-gray-400">
                  <Wifi className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">暂无可用账号，请先在 VPN 管理页面添加</p>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">选择账号</label>
                  <select value={selectedPoolId} onChange={e => setSelectedPoolId(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                    <option value="">请选择</option>
                    {vpnPool.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.account_name} {p.notes ? `(${p.notes})` : ''} {p.expired_at ? `- 到期 ${new Date(p.expired_ts * 1000).toLocaleDateString()}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="p-5 border-t flex gap-3">
              <button onClick={() => setShowVpnAssign(false)}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                取消
              </button>
              <button onClick={handleAssignVpn} disabled={vpnLoading || !selectedPoolId}
                className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                {vpnLoading ? '处理中...' : '确认分配'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomersPage;